import { selectRouterQueryParameters } from '@noths/polaris-client-next-redux';
import { selectIsFeatureFlagVariation } from '@noths/polaris-client-user-configuration';
import { Link, ProductsAPIPath } from '@noths/polaris-dev-ts-types';
import { createSelector } from '@reduxjs/toolkit';
import { ParsedUrlQueryInput } from 'querystring';
import * as R from 'ramda';

import { FilterUid } from 'src/components/organisms/Filter/constants/uids';
import { selectSelectedFilterOptionsWithoutSortFilter } from 'src/components/organisms/Filter/modules/selectors/selectors';
import { SearchProvider } from 'src/constants/featureFlags';
import { selectPageType } from 'src/redux/navigation';
import { FilterOption, PageType } from 'src/types';
import { ReduxApplicationState } from '../combinedReducer';
import { ProductsAPIResponse } from './types';

export const selectRequestOptionsAsQueryObject = (
  state: ReduxApplicationState,
): ParsedUrlQueryInput => {
  const {
    filterMenu: { selectedSortOption },
    products: { searchTerm },
  } = state;

  const selectedFilterOptions = selectSelectedFilterOptionsWithoutSortFilter(state);

  const queryObject = selectedFilterOptions.reduce(
    (acc, value) =>
      value.selectedByDefault
        ? acc
        : {
            ...acc,
            [value.filterOptionUid || value.filterUid]: value.filterOptionValue,
          },
    {},
  );

  const { previewing } = selectRouterQueryParameters(state);

  if (previewing) {
    queryObject['previewing'] = true;
  }

  if (selectedSortOption) {
    queryObject['sort'] = selectedSortOption.id;
  }

  if (searchTerm) {
    queryObject['term'] = searchTerm;
  }

  return queryObject;
};

export const selectFilterOptions = (state: ReduxApplicationState, filterUid: FilterUid) =>
  R.compose(
    R.map((option: FilterOption) => ({ href: option.value, title: option.title, rel: '' } as Link)),
    R.propOr([], 'options'),
    R.find(R.propEq('uid', filterUid)),
  )(state.filter.collections.filters);

export const selectSelectedFilterOptions = (state: ReduxApplicationState, filterUid: FilterUid) =>
  state.filter.collections.selectedFilterOptions.find((f) => f.filterUid === filterUid)
    ?.filterOptionValue;

export const selectNextPageNumber = ({ products }: ReduxApplicationState) =>
  products.currentPage + 1;

export const selectPreviousPageNumber = ({ products }: ReduxApplicationState) =>
  parseInt(Object.keys(products.items)[0]) - 1;

export const selectBrowseApiPath = (state: ReduxApplicationState): ProductsAPIPath | undefined => {
  switch (selectPageType(state)) {
    case PageType.Search:
      return ProductsAPIPath.Search;
    case PageType.Category:
      return ProductsAPIPath.Category;
    case PageType.Partner:
      return ProductsAPIPath.Partner;
    default:
      return undefined;
  }
};

export const selectPartnerShortcode = ({ products }: ReduxApplicationState) =>
  products.partner?.shortcode ?? '';

export const selectPartnerName = ({ products }: ReduxApplicationState) =>
  products.partner?.name ?? '';

export const selectPartnerMetaTagDescription = ({ products }: ReduxApplicationState) =>
  products.partner?.metaTagDescription ?? '';

export const selectPartnerLogoUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.logo ?? '';

const PARTNER_HOMEPAGE_LINK_INDEX = 0;
const PARTNER_PRODUCTS_LINK_INDEX = 1;

export const selectPartnerHomepageUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.links[PARTNER_HOMEPAGE_LINK_INDEX]?.href ?? '';

export const selectPartnerHomepageRelativeUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.links[PARTNER_HOMEPAGE_LINK_INDEX]?.rel ?? '';

export const selectPartnerProductsUrl = ({ products }: ReduxApplicationState) =>
  products.partner?.links[PARTNER_PRODUCTS_LINK_INDEX]?.href ?? '';

export const selectProductFromCode = createSelector(
  [
    ({ products }: ReduxApplicationState) => products.items,
    (_, productCode: number) => productCode,
  ],
  (products, productCode) =>
    Object.values(products)
      .flat()
      .find(({ code }) => code === productCode),
);

export const selectSearchProvider = (state: ReduxApplicationState) =>
  selectIsFeatureFlagVariation(state, SearchProvider.NAME, SearchProvider.GOOGLE)
    ? SearchProvider.GOOGLE
    : SearchProvider.LOOP54;

export const selectSearchProviderRequestHeader = (state: ReduxApplicationState) => ({
  'search-provider': selectIsFeatureFlagVariation(state, SearchProvider.NAME, SearchProvider.GOOGLE)
    ? 'google'
    : 'loop54',
});

export const selectDataFromProductsAPIResponse = (productApiResponse: ProductsAPIResponse) => {
  const {
    category,
    currencyCode,
    products: items,
    currentPage,
    totalPages,
    filters,
    totalProducts,
    productsReturnedCount,
    queryType,
    sortingOptions,
    navigation,
    partner,
  } = productApiResponse;

  return {
    category,
    currencyCode,
    items: { [currentPage]: items },
    filters,
    currentPage,
    totalPages,
    totalProducts,
    productsReturnedCount,
    queryType,
    sortingOptions,
    navigation,
    partner,
  };
};

export const selectShowFeaturedProduct = ({
  products: { featuredProduct },
}: ReduxApplicationState) =>
  !!featuredProduct && featuredProduct.inventoryStatus !== 'out_of_stock';
